import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import API from '../services/api';
import "./TasksView.css";

const TasksView = ({
  filter,
  setFilter,
  selectedFilter,
  setSelectedFilter,
  applyFilter,
  searchQuery,
  setSearchQuery,
  pendingTasks,
  missedTasks,
  completedTasks,
  setIsModalOpen,
  resetNewTaskForm,
  setSelectedTask,
  setIsViewModalOpen,
  setIsEditMode,
  handleUpdateStatus,
  handleDelete,
  deletingTaskId,
  updatingTaskId,
  setNewTask,
  handleSave
}) => {
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [isBulkLoading, setIsBulkLoading] = useState(false);
  const [bulkLoadingAction, setBulkLoadingAction] = useState(null); // 'complete' | 'delete' | null
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const fileInputRef = useRef(null);
  const actionMenuRef = useRef(null);
  const filterMenuRef = useRef(null);
  const token = localStorage.getItem('token');

  const visibleTasks = (filter === "pending" ? pendingTasks : filter === "missed" ? missedTasks : completedTasks)
    .filter((t) => t.title.toLowerCase().includes((searchQuery || '').toLowerCase()));

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showActionMenu && actionMenuRef.current && !actionMenuRef.current.contains(e.target)) {
        setShowActionMenu(false);
      }
      if (showFilterMenu && filterMenuRef.current && !filterMenuRef.current.contains(e.target)) {
        setShowFilterMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showActionMenu, showFilterMenu]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setIsProcessingImage(true);
    toast.info('Cortex is analyzing your image...');

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      console.log('Starting image upload...');
      console.log('File:', file.name, file.size, 'bytes');
      console.log('Token:', token ? 'Present' : 'Missing');
      console.log('Sending to:', '/tasks/ai/parse-image');

      const response = await API.post('/tasks/ai/parse-image', formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Response received:', response.data);

      const { parsed_task, extracted_text } = response.data;

      // Auto-fill the modal with AI-parsed data
      setNewTask({
        title: parsed_task?.title || extracted_text?.split('\n')[0] || 'Task from Image',
        description: parsed_task?.description || extracted_text || 'Task created from image',
        due_date: '',
        priority: parsed_task?.priority || 'medium'
      });

      setIsModalOpen(true);
      toast.success(`Cortex extracted task! Category: ${parsed_task?.category || 'general'}`);
    } catch (err) {
      console.error('Image parsing error:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
        url: err.config?.url,
        baseURL: err.config?.baseURL
      });
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to process image';
      toast.error(errorMsg);
    } finally {
      setIsProcessingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const toggleSelectMode = () => {
    setSelectMode((prev) => {
      if (prev) setSelectedTaskIds([]);
      return !prev;
    });
  };

  const bulkTargetComplete = filter !== 'completed';

  const toggleTaskSelection = (taskId) => {
    setSelectedTaskIds((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    );
  };

  const exitSelection = () => {
    setSelectMode(false);
    setSelectedTaskIds([]);
  };

  const getFilterLabel = () => {
    if (selectedFilter === 'name') return 'By Name';
    if (selectedFilter === 'date') return 'By Date';
    return 'All tasks';
  };

  const handleFilterChange = (value) => {
    setSelectedFilter(value);
    applyFilter(value);
    setShowFilterMenu(false);
  };

  const handleSelectAll = () => {
    if (!visibleTasks.length) {
      toast.info('No tasks to select');
      setShowActionMenu(false);
      return;
    }
    setSelectMode(true);
    setSelectedTaskIds(visibleTasks.map((task) => task.id));
    setShowActionMenu(false);
  };

  const handleDeselectAll = () => {
    setSelectedTaskIds([]);
    setShowActionMenu(false);
  };

  const hasSelection = selectedTaskIds.length > 0;

  const handleBulkComplete = async () => {
    if (!selectedTaskIds.length) {
      toast.info('Select at least one task');
      return;
    }

    setIsBulkLoading(true);
    setBulkLoadingAction('complete');
    try {
      await API.post(
        '/tasks/bulk/status',
        { task_ids: selectedTaskIds, is_completed: bulkTargetComplete },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await applyFilter(selectedFilter);
      toast.success(bulkTargetComplete ? 'Marked selected tasks complete' : 'Restored selected tasks');
      exitSelection();
    } catch (err) {
      const msg = err.response?.data?.detail || (bulkTargetComplete ? 'Bulk complete failed' : 'Bulk restore failed');
      toast.error(msg);
    } finally {
      setIsBulkLoading(false);
      setBulkLoadingAction(null);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedTaskIds.length) {
      toast.info('Select at least one task');
      return;
    }

    setIsBulkLoading(true);
    setBulkLoadingAction('delete');
    try {
      await API.post(
        '/tasks/bulk/delete',
        { task_ids: selectedTaskIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await applyFilter(selectedFilter);
      toast.success('Deleted selected tasks');
      exitSelection();
      setShowBulkDeleteConfirm(false);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Bulk delete failed';
      toast.error(msg);
    } finally {
      setIsBulkLoading(false);
      setBulkLoadingAction(null);
    }
  };

  return (
    <div className="tasks-view-container" style={{width: '100%'}}>
      <div className="tasks-header">
        <h3 className="list-title">ALL TASKS</h3>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: 'none' }}
          />
          <button 
            className="add-task-btn-top ai-btn" 
            onClick={() => fileInputRef.current?.click()}
            disabled={isProcessingImage}
            style={{
              background: isProcessingImage 
                ? 'rgba(255,255,255,0.1)' 
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              cursor: isProcessingImage ? 'not-allowed' : 'pointer',
              opacity: isProcessingImage ? 0.6 : 1
            }}
          >
            {isProcessingImage ? 'Processing' : 'Image to Task'}
          </button>
          <button className="add-task-btn-top" onClick={() => {
            resetNewTaskForm();
            setIsModalOpen(true);
          }}>Add New Task</button>
        </div>
      </div>

      <div className="filter-tabs-container">
        {['pending', 'missed', 'completed'].map((type) => (
          <button key={type} className={`filter-tab ${filter === type ? 'active-tab' : ''}`} onClick={() => setFilter(type)}>
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </button>
        ))}
      </div>

      <div className="search-filter-wrapper">
        <input
          type="text"
          placeholder="Search tasks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
        <div className="inline-filter-actions">
          <div className="filter-menu-wrapper filter-select-wrapper" ref={filterMenuRef}>
            <button
              className={`filter-select-btn ${showFilterMenu ? 'active' : ''}`}
              onClick={() => setShowFilterMenu((prev) => !prev)}
              title="Filter tasks"
            >
              <span className="filter-select-label">{getFilterLabel()}</span>
              <span className="filter-select-chevron">⌄</span>
            </button>
            {showFilterMenu && (
              <div className="filter-menu-dropdown">
                <button
                  className={`filter-menu-item ${selectedFilter === 'none' ? 'active-item' : ''}`}
                  onClick={() => handleFilterChange('none')}
                >
                  All tasks
                </button>
                <button
                  className={`filter-menu-item ${selectedFilter === 'name' ? 'active-item' : ''}`}
                  onClick={() => handleFilterChange('name')}
                >
                  By Name
                </button>
                <button
                  className={`filter-menu-item ${selectedFilter === 'date' ? 'active-item' : ''}`}
                  onClick={() => handleFilterChange('date')}
                >
                  By Date
                </button>
              </div>
            )}
          </div>
          <div className="filter-menu-wrapper" ref={actionMenuRef}>
            <button
              className={`filter-menu-btn ${selectMode || showActionMenu ? 'active' : ''}`}
              onClick={() => setShowActionMenu((prev) => !prev)}
              title="More actions"
            >
              <span className="filter-menu-icon">⋮</span>
            </button>
            {showActionMenu && (
              <div className="filter-menu-dropdown">
                <button
                  className="filter-menu-item"
                  onClick={() => {
                    toggleSelectMode();
                    setShowActionMenu(false);
                  }}
                >
                  {selectMode ? 'Exit selection mode' : 'Select individually'}
                </button>
                <button
                  className="filter-menu-item"
                  onClick={selectedTaskIds.length === visibleTasks.length && visibleTasks.length > 0 ? handleDeselectAll : handleSelectAll}
                >
                  {selectedTaskIds.length === visibleTasks.length && visibleTasks.length > 0 ? 'Deselect all' : 'Select all'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectMode && (
        <div className="bulk-actions-bar">
          <span>{selectedTaskIds.length} selected</span>
          <div
            className="bulk-actions-buttons"
            style={{
              visibility: hasSelection ? 'visible' : 'hidden',
              pointerEvents: hasSelection ? 'auto' : 'none'
            }}
          >
            <button
              className="bulk-btn complete"
              onClick={handleBulkComplete}
              disabled={isBulkLoading || !hasSelection}
            >
              {bulkLoadingAction === 'complete' ? (
                <div className="mini-spinner" aria-label="loading" />
              ) : bulkTargetComplete ? 'Complete' : 'Restore'}
            </button>
            <button
              className="bulk-btn delete"
              onClick={() => {
                if (!hasSelection) return;
                setShowBulkDeleteConfirm(true);
              }}
              disabled={isBulkLoading || !hasSelection}
            >
              {bulkLoadingAction === 'delete' ? (
                <div className="mini-spinner" aria-label="loading" />
              ) : 'Delete'}
            </button>
          </div>
        </div>
      )}

      {showBulkDeleteConfirm && (
        <div className="modal-overlay" onClick={() => !isBulkLoading && setShowBulkDeleteConfirm(false)}>
          <div
            className="modal-content ultra-glass danger-mode"
            style={{ maxWidth: '440px', width: '90%', textAlign: 'center', padding: '36px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-btn" onClick={() => !isBulkLoading && setShowBulkDeleteConfirm(false)}>✕</button>

            <h3 className="modal-title" style={{ marginBottom: '14px' }}>
              Delete {selectedTaskIds.length} task{selectedTaskIds.length === 1 ? '' : 's'}?
            </h3>

            <p style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '28px' }}>
              This action cannot be undone.
            </p>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                className="save-btn cancel-btn"
                onClick={() => !isBulkLoading && setShowBulkDeleteConfirm(false)}
                style={{
                  background: document.documentElement.getAttribute('data-theme') === 'light'
                    ? '#ffffff'
                    : 'rgba(255,255,255,0.05)',
                  color: document.documentElement.getAttribute('data-theme') === 'light'
                    ? '#202124'
                    : '#ffffff',
                  flex: 1,
                  cursor: isBulkLoading ? 'not-allowed' : 'pointer',
                  marginTop: 0
                }}
                disabled={isBulkLoading}
              >
                Cancel
              </button>

              <button
                className="save-btn"
                onClick={handleBulkDelete}
                disabled={isBulkLoading}
                style={{
                  background: isBulkLoading
                    ? (document.documentElement.getAttribute('data-theme') === 'light' ? 'rgba(223, 100, 100, 0.7)' : 'rgba(255, 107, 107, 0.7)')
                    : (document.documentElement.getAttribute('data-theme') === 'light' ? '#ff7b7b' : '#ce7070'),
                  color: '#fff',
                  border: 'none',
                  flex: 1,
                  cursor: isBulkLoading ? 'not-allowed' : 'pointer',
                  opacity: isBulkLoading ? 0.85 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  minHeight: '45px',
                  marginTop: 0
                }}
              >
                {isBulkLoading ? (
                  <div style={{
                    width: '18px',
                    height: '18px',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderTop: '2px solid #fff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                ) : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="task-grid">
        {/* --- UPDATED TASK CARD CODE --- */}
{visibleTasks.map(task => (
    <div
  className={`modern-task-card ${filter} ${selectMode && selectedTaskIds.includes(task.id) ? 'selected-card' : ''}`}
  key={task.id}
  onClick={() => {
    if (selectMode) {
      toggleTaskSelection(task.id);
      return;
    }
    if (updatingTaskId === task.id || deletingTaskId === task.id) return;
    setSelectedTask(task);
    setIsViewModalOpen(true);
    setIsEditMode(false);
  }}
  style={{
    cursor: (updatingTaskId === task.id || deletingTaskId === task.id) ? 'not-allowed' : 'pointer',
    height: '220px',        // Reduced height for compact cards
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden'
  }}
>
  {selectMode && (
    <label className="select-checkbox" onClick={(e) => e.stopPropagation()}>
      <input
        type="checkbox"
        checked={selectedTaskIds.includes(task.id)}
        onChange={() => toggleTaskSelection(task.id)}
      />
      <span className="checkbox-custom"></span>
    </label>
  )}

  {/* 1. YE HAI WOH PYARI SI STRIP (Edge color) */}
  <div className={`status-strip ${filter}`} style={{
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '4px',           // Side bar ki width
    backgroundColor: filter === 'pending' ? '#00e0ff' : filter === 'missed' ? '#ff4b2b' : '#00ff00',
    boxShadow: '0 0 10px rgba(0, 224, 255, 0.5)'
  }}></div>

  <div className="card-content" style={{
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: '15px 15px 15px 20px'
  }}>
    <h4 className="task-title-text" style={{ marginBottom: '8px' }}>{task.title}</h4>

    <p className="task-desc-text" style={{
      height: '45px',
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      marginBottom: '10px',
      color: 'rgba(255,255,255,0.6)'
    }}>
      {task.description}
    </p>

    {/* 2. DUE DATE KA ASLI DESIGN */}
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <span style={{
        fontSize: '9px',
        color: '#00e0ff',
        fontWeight: '800',
        letterSpacing: '1px',
        opacity: 0.8
      }}>
        DUE DATE
      </span>
      <span className="due-date-info" style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(8px)',
        color: '#ffffff',
        padding: '6px 14px',
        borderRadius: '6px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        fontSize: '13px',
        fontWeight: '600',
        display: 'inline-block',
        width: 'fit-content',
        fontFamily: 'monospace'
      }}>
        {task.due_date ? new Date(task.due_date).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }) : "NO DEADLINE"}
      </span>
    </div>

    {/* 3. BUTTONS (Lock at bottom with original classes) */}
    <div className="premium-actions" style={{
      display: 'flex',
      gap: '8px',
      marginTop: 'auto' // Ye magic line hai jo buttons hamesha neechy rakhy gi
    }}>
      <button
        className="action-btn-wide complete"
        onClick={(e) => {
          e.stopPropagation();
          handleUpdateStatus(task.id, filter !== 'completed');
        }}
        disabled={updatingTaskId === task.id || deletingTaskId === task.id || selectMode}
        style={{
          opacity: (updatingTaskId === task.id || deletingTaskId === task.id) ? 0.5 : 1,
          cursor: (updatingTaskId === task.id || deletingTaskId === task.id) ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
      >
        {updatingTaskId === task.id ? (
          <div style={{
            width: '14px',
            height: '14px',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderTop: '2px solid #fff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
        ) : (filter === 'completed' ? 'Undo' : 'Complete')}
      </button>

      <button
        className="action-btn-wide delete"
        onClick={(e) => {
          e.stopPropagation();
          handleDelete(task.id);
        }}
        disabled={deletingTaskId === task.id || updatingTaskId === task.id || selectMode}
        style={{
          opacity: (deletingTaskId === task.id || updatingTaskId === task.id) ? 0.5 : 1,
          cursor: (deletingTaskId === task.id || updatingTaskId === task.id) ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
      >
        {deletingTaskId === task.id ? (
          <div style={{
            width: '14px',
            height: '14px',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderTop: '2px solid #fff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
        ) : 'Delete'}
      </button>
    </div>
  </div>
</div>
  ))}
      </div>
    </div>
  );
};

export default TasksView;